/**
 * Data shared by the APRA / operational readiness quiz and results bundles.
 * Copied from the agency build; keep identical to the agency tables so future drops diff cleanly.
 */

export var ASSESSMENT_NAME = "Operational Readiness";

/**
 * Date-gated copy: returns `after` once the client clock passes `when`.
 * @param {string} when
 * @param {any} before
 * @param {any} after
 */
export function dated(when, before, after) {
  return new Date() >= new Date(when) ? after : before;
}

export var CELLS = {
  "au-fsi": {
    built: true,
    geo: "au",
    overlay: null,
    domainRemap: {
      S4: "cps234",
      S5: "cps234",
      S6: "soci",
      S12: "soci",
      S10: "ops",
    },
  },
  "au-fsi-lite": { built: true, geo: "au", overlay: null },
  "au-telco": { built: true, geo: "au", overlay: "telco" },
  "au-ecom": { built: true, geo: "au", overlay: "ecom" },
  "au-digital": { built: true, geo: "au", overlay: "digital" },
  "au-digital-lite": { built: true, geo: "au", overlay: "digital" },
  "au-generic": { built: true, geo: "au", overlay: null, generic: true },
  "sg-fsi": { built: true, geo: "sg", overlay: null },
  "sg-telco": {
    built: true,
    geo: "sg",
    overlay: "telco",
    overlayExclude: ["T3"],
  },
  "sg-ecom": { built: true, geo: "sg", overlay: "ecom" },
  "sg-digital": { built: true, geo: "sg", overlay: "digital" },
  "sg-generic": { built: true, geo: "sg", overlay: null, generic: true },
  "nz-fsi": { built: true, geo: "nz", overlay: null },
  "nz-telco": {
    built: true,
    geo: "nz",
    overlay: "telco",
    overlayExclude: ["T3"],
  },
  "nz-ecom": { built: true, geo: "nz", overlay: "ecom" },
  "nz-digital": { built: true, geo: "nz", overlay: "digital" },
  "nz-generic": { built: true, geo: "nz", overlay: null, generic: true },
  "hk-fsi": { built: true, geo: "hk", overlay: null },
  "hk-telco": {
    built: true,
    geo: "hk",
    overlay: "telco",
    overlayExclude: ["T3"],
  },
  "hk-ecom": { built: true, geo: "hk", overlay: "ecom" },
  "hk-digital": { built: true, geo: "hk", overlay: "digital" },
  "hk-generic": { built: true, geo: "hk", overlay: null, generic: true },
  "my-fsi": { built: true, geo: "my", overlay: null },
  "my-telco": {
    built: true,
    geo: "my",
    overlay: "telco",
    overlayExclude: ["T3"],
  },
  "my-ecom": { built: true, geo: "my", overlay: "ecom" },
  "my-digital": { built: true, geo: "my", overlay: "digital" },
  "my-generic": { built: true, geo: "my", overlay: null, generic: true },
  "th-fsi": { built: true, geo: "th", overlay: null },
  "th-telco": {
    built: true,
    geo: "th",
    overlay: "telco",
    overlayExclude: ["T3"],
  },
  "th-ecom": { built: true, geo: "th", overlay: "ecom" },
  "th-digital": { built: true, geo: "th", overlay: "digital" },
  "th-generic": { built: true, geo: "th", overlay: null, generic: true },
  "kr-fsi": { built: true, geo: "kr", overlay: null },
  "kr-telco": {
    built: true,
    geo: "kr",
    overlay: "telco",
    overlayExclude: ["T3"],
  },
  "kr-ecom": { built: true, geo: "kr", overlay: "ecom" },
  "kr-digital": { built: true, geo: "kr", overlay: "digital" },
  "kr-generic": { built: true, geo: "kr", overlay: null, generic: true },

  "other-fsi": { built: true, geo: "other", overlay: null, generic: true },
  "other-telco": {
    built: true,
    geo: "other",
    overlay: "telco",
    generic: true,
    overlayExclude: ["T3"],
  },
  "other-ecom": { built: true, geo: "other", overlay: "ecom", generic: true },
  "other-digital": {
    built: true,
    geo: "other",
    overlay: "digital",
    generic: true,
  },
  "other-generic": {
    built: true,
    geo: "other",
    overlay: null,
    generic: true,
  },
  default: { built: true, geo: null, overlay: null, generic: true },
};

export var CONFIRM_BY_CELL = {
  "au-fsi": {
    sub: "Use your report to identify gaps and plan next steps across CPS 230, CPS 234 and operational resilience.",
    tiles: [
      {
        title: "The CPS 230 guide",
        desc: "What CPS 230 asks, where accountability sits, and the evidence each obligation calls for. Links through to the practitioner version.",
        url: "https://utm.io/cps230-dms",
        cta: "Read the guide",
      },
      {
        title: "What AI changes about incident response",
        desc: "AI is already inside claims, payments and fraud workflows. What gets asked when one misbehaves, and what your operating record needs to show.",
        url: "https://utm.io/ai-blog",
        cta: "Read the article",
      },
      {
        title: "Ahead of the curve: AFR intelligence report",
        desc: "See why leading organisations are shifting from reactive response to proactive resilience, and what that means for your team.",
        url: "https://www.pagerduty.com/resources/ai/whitepaper/pagerdutyafrintelligence2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From regulation to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "au-fsi-lite": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the Consumer Data Right, the Notifiable Data Breaches scheme and AI governance.",
    tiles: [
      {
        title: "What AI changes about incident response",
        desc: "AI is already inside claims, payments and fraud workflows. What gets asked when one misbehaves, and what your operating record needs to show.",
        url: "https://utm.io/ai-blog",
        cta: "Read the article",
      },
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services - without the prudential jargon.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "The state of service reliability in Australia",
        desc: "How Australian organisations are performing on reliability, and where the leaders are pulling ahead.",
        url: "https://www.pagerduty.com/resources/ai/whitepaper/state-of-service-reliability-australia/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "au-telco": {
    sub: "Use your report to identify gaps and plan next steps across the SOCI Act's telecommunications security and incident-reporting rules, the Privacy Act and AI governance.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "The state of service reliability in Australia",
        desc: "How Australian organisations are performing on reliability, and where the leaders are pulling ahead.",
        url: "https://www.pagerduty.com/resources/ai/whitepaper/state-of-service-reliability-australia/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "au-ecom": {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, the Notifiable Data Breaches scheme, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "The state of service reliability in Australia",
        desc: "How Australian organisations are performing on reliability, and where the leaders are pulling ahead.",
        url: "https://www.pagerduty.com/resources/ai/whitepaper/state-of-service-reliability-australia/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  },
  "au-digital": {
    sub: "Use your report to identify gaps and plan next steps across CPS 230 flow-down obligations, the SOCI Act's data-storage duties, the Notifiable Data Breaches scheme and AI governance.",
    tiles: [
      {
        title: "The CPS 230 guide",
        desc: "What CPS 230 asks, where accountability sits, and the evidence each obligation calls for. Links through to the practitioner version.",
        url: "https://utm.io/cps230-dms",
        cta: "Read the guide",
      },
      {
        title: "What AI changes about incident response",
        desc: "AI is already inside claims, payments and fraud workflows. What gets asked when one misbehaves, and what your operating record needs to show.",
        url: "https://utm.io/ai-blog",
        cta: "Read the article",
      },
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  },
  "au-generic": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the Cyber Security Act's reporting duties, the Notifiable Data Breaches scheme and AI governance.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "The state of service reliability in Australia",
        desc: "How Australian organisations are performing on reliability, and where the leaders are pulling ahead.",
        url: "https://www.pagerduty.com/resources/ai/whitepaper/state-of-service-reliability-australia/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "sg-fsi": {
    sub: "Use your report to identify gaps and plan next steps across MAS technology-risk and business-continuity guidelines, the PDPA and FEAT principles for AI.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "sg-telco": {
    sub: "Use your report to identify gaps and plan next steps across the Cybersecurity Act's critical-infrastructure duties, the PDPA and Singapore's Model AI Governance Framework.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "sg-ecom": {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, the PDPA, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  },
  "sg-digital": {
    sub: "Use your report to identify gaps and plan next steps across the Cybersecurity Act's critical-infrastructure and cloud duties, the PDPA and Singapore's Model AI Governance Framework.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  },
  "sg-generic": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the PDPA and Singapore's Model AI Governance Framework.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "nz-fsi": {
    sub: "Use your report to identify gaps and plan next steps across CoFI conduct licensing, RBNZ outsourcing and operational-risk standards, the Privacy Act 2020 and AI readiness.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "nz-telco": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, NCSC cyber-resilience guidance, the Privacy Act 2020 and AI readiness.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "nz-ecom": {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, the Privacy Act 2020, PCI DSS and AI readiness.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  },
  "nz-digital": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the Privacy Act 2020, NCSC cyber guidance and AI readiness.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  },
  "nz-generic": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the Privacy Act 2020 and AI readiness.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "hk-fsi": {
    sub: "Use your report to identify gaps and plan next steps across HKMA OR-2 operational resilience, C-RAF and TM-E-1 cyber expectations, Cap. 653 and the PDPO.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From regulation to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "hk-telco": {
    sub: "Use your report to identify gaps and plan next steps across Cap. 653's critical-infrastructure duties, OFCA obligations, the PDPO and AI readiness.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "hk-ecom": {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, the PDPO, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  },
  "hk-digital": {
    sub: "Use your report to identify gaps and plan next steps across HKMA outsourcing flow-down, Cap. 653, the PDPO and AI governance.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  },
  "hk-generic": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, Cap. 653, the PDPO and AI readiness.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "my-fsi": {
    sub: "Use your report to identify gaps and plan next steps across BNM RMiT, the Cyber Security Act 2024, the PDPA and AI readiness.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From regulation to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "my-telco": {
    sub: "Use your report to identify gaps and plan next steps across the Cyber Security Act's NCII duties, MCMC obligations, the PDPA and AI readiness.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "my-ecom": {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, the PDPA, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  },
  "my-digital": {
    sub: "Use your report to identify gaps and plan next steps across NCII flow-down, the PDPA and AI governance.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  },
  "my-generic": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the Cyber Security Act 2024, the PDPA and AI readiness.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "th-fsi": {
    sub: "Use your report to identify gaps and plan next steps across the Bank of Thailand's IT risk guidelines, the Cybersecurity Act, the PDPA and AI readiness.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From regulation to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "th-telco": {
    sub: "Use your report to identify gaps and plan next steps across the Cybersecurity Act's CII duties, NBTC obligations, the PDPA and AI readiness.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "th-ecom": {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, the PDPA, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  },
  "th-digital": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the Cybersecurity Act, the PDPA and AI governance.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  },
  "th-generic": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, the PDPA and AI readiness.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "kr-fsi": {
    sub: "Use your report to identify gaps and plan next steps across the e-finance incident regime, PIPA and the AI Basic Act.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From regulation to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  },
  "kr-telco": {
    sub: "Use your report to identify gaps and plan next steps across network-stability obligations, PIPA and the AI Basic Act.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  "kr-ecom": {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, PIPA, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  },
  "kr-digital": {
    sub: "Use your report to identify gaps and plan next steps across customer evidence, PIPA and the AI Basic Act.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  },
  "kr-generic": {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, PIPA and the AI Basic Act.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
  default: {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, data-protection and incident-reporting obligations and AI governance.",
    tiles: [
      {
        title: "5 steps to reduce operational risk and build resilience",
        desc: "A practical checklist for tightening detection, response and recovery across your critical operations.",
        url: "https://www.pagerduty.com/resources/digital-operations/checklist/5-steps-to-reduce-operational-risk-and-build-resilience/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  },
};

export var DISCLAIMER =
  "This assessment is provided for general information only. It does not constitute legal, regulatory or compliance advice, and results do not represent the view of any regulator. Consult qualified legal or compliance professionals about your organisation's specific obligations.";

export var DOMAINS = {
  ops: {
    name: "Operational resilience",
    subtitle: "Critical operations & continuity",
    radarLines: ["Operational", "resilience"],
    deadline: "",
    regRisk:
      "Regulators and enterprise customers increasingly expect you to know which of your operations are critical, and how long each can be disrupted before it matters. When something breaks, the first questions asked are how fast you knew and how fast you recovered.",
    whatGoodLooksLike:
      "Keep the service catalogue current, with an owner and a defined tolerance for each critical operation. Map those operations to the systems, people and third parties they depend on, and test the recovery path periodically.",
    supportsLine:
      "PagerDuty covers mobilisation and evidence: it turns an alert your monitoring raises into an escalation to the right responder, and keeps a timestamped record of what happened next.",
    resource: null,
  },
  cps234: {
    name: "CPS 234",
    subtitle: "Information security",
    radarLines: ["CPS 234"],
    deadline:
      "In effect - 72-hour notification window applies to all material security incidents.",
    regRisk:
      "APRA requires notification within 72 hours of becoming aware of a material information security incident. Meeting that depends on how quickly an incident is detected, and how reliably it reaches someone who can act on it.",
    whatGoodLooksLike:
      "Correlating and deduplicating alerts cuts the time spent separating real problems from noise, so on-call responders see fewer duplicate and low-priority pages. Give someone ownership of alert quality, so thresholds and routing are reviewed as services change.",
    supportsLine:
      "PagerDuty covers the path from alert to notification: it correlates alerts, escalates them automatically, and keeps a timestamped trail that supports regulatory reporting.",
    resource: {
      label: "APRA CPS 234 Information Security Standard",
      url: "https://handbook.apra.gov.au/standard/cps-234",
    },
  },
  soci: {
    name: "SOCI Act",
    subtitle: "Critical infrastructure",
    radarLines: ["SOCI Act"],
    deadline:
      "In effect - Critical cyber incidents must be reported to the ACSC within 12 hours.",
    regRisk:
      "SOCI Act requires critical cyber incidents to be reported to the ACSC within 12 hours. That clock runs overnight and at weekends, so meeting it depends on cover outside business hours.",
    whatGoodLooksLike:
      "Test the response outside business hours too, across time zones and at weekends. On-call routing is automated, escalation paths are defined, and reporting templates are written before anyone needs them.",
    supportsLine:
      "PagerDuty covers mobilisation and evidence: automated out-of-hours escalation, and a timestamped record that supports ACSC reporting.",
    resource: {
      label: "CISC Critical Infrastructure Obligations Guidance",
      url: "https://www.cisc.gov.au/legislation-regulation-and-compliance/soci-act-2018",
    },
  },
  security: {
    name: "Security & reporting",
    subtitle: "Detection, response & notification",
    radarLines: ["Security &", "reporting"],
    deadline: "",
    regRisk:
      "Many of the regimes covered here set a defined notification period, and some require it within a matter of hours. How quickly you detect an incident, and how quickly it reaches the right people, affects whether {SEC_REG} hears about it from you or from someone else.",
    whatGoodLooksLike:
      "Correlating and deduplicating alerts cuts the time spent separating real problems from noise, so on-call responders see fewer duplicate and low-priority pages. Give someone ownership of alert quality, so thresholds and routing are reviewed as services change.",
    supportsLine:
      "PagerDuty covers the path from alert to notification: it correlates alerts, escalates them automatically, and keeps a timestamped trail that supports regulatory reporting.",
    resource: null,
  },
  privacy: {
    name: "Privacy",
    subtitle: "Breach response",
    radarLines: ["Privacy"],
    deadline: "",
    regRisk:
      "Privacy regulators are increasing penalties and enforcement scrutiny. A slower response can make a breach harder to contain and harder to scope, and {PRIVACY_BODY} expects notification without undue delay.",
    whatGoodLooksLike:
      "Security, legal and communications should not have to wait on each other before starting. One incident declaration brings in all three, so containment, scoping and the notification decision can run together.",
    supportsLine:
      "PagerDuty supports the coordination part of a breach response: declaring the incident, bringing security, legal and communications in at the same time, and keeping a record of who responded and when.",
    resource: null,
  },
  ai: {
    name: "AI readiness",
    subtitle: "AI in critical operations",
    radarLines: ["AI", "readiness"],
    deadline: "",
    regRisk:
      "Regulators are moving from principles to expectations on AI: who owns each AI use case, what happens when a model misbehaves inside a critical operation, and how quickly a human can take over. AI failures now need to be part of operational response planning.",
    whatGoodLooksLike:
      "Keep a live inventory of AI use cases, with a named owner for each one. Monitor model behaviour in production, and treat an AI failure like any other incident, with a fallback that has been tested.",
    supportsLine:
      "PagerDuty covers the step from alert to human: when your monitoring flags an AI-driven service misbehaving, PagerDuty escalates it to a named owner and handles it like any other incident.",
    resource: null,
  },
  svc: {
    name: "Service continuity",
    subtitle: "Network & emergency obligations",
    radarLines: ["Service", "continuity"],
    deadline: "",
    regRisk:
      "Telecommunications regulators treat network continuity and emergency-call delivery as core obligations, with outage-communication requirements and penalties attached. During a major outage the response and the customer communication are judged separately, and both are on the clock.",
    whatGoodLooksLike:
      "Emergency-call impact is picked up by monitoring rather than reported in. Playbooks are rehearsed and their steps timestamped, and customer communication is owned separately from the engineering response so neither waits on the other.",
    supportsLine:
      "PagerDuty covers mobilisation and evidence: it turns service-impact alerts into a rehearsed escalation, and keeps a timestamped record for the regulator.",
    resource: null,
  },
  peak: {
    name: "Peak & payments",
    subtitle: "Revenue-critical trading",
    radarLines: ["Peak &", "payments"],
    deadline: "",
    regRisk:
      "No regulator sets your checkout uptime - your P&L does. Every minute of degraded checkout at peak has a dollar figure, and payment-provider dependencies mean the fault is often not in your own stack. The test is how fast you know, and how fast you can say it's them, not you.",
    whatGoodLooksLike:
      "Detection is tuned for the biggest trading hours rather than an average day. There is an agreed cost-per-minute threshold that escalates on its own, and provider health is correlated with your own telemetry so a third-party fault can be identified quickly.",
    supportsLine:
      "PagerDuty covers detection and escalation: peak-tuned alerting, automatic escalation as soon as your monitoring flags trouble, and correlation of provider signals with your own.",
    resource: null,
  },
  cust: {
    name: "Customer obligations",
    subtitle: "Enterprise & regulatory flow-down",
    radarLines: ["Customer", "obligations"],
    deadline: "",
    regRisk:
      "Your enterprise customers answer to regulators - and their obligations flow down through contracts. When their auditor asks them to evidence YOUR incident response, 'trust us' is not an acceptable answer. Timestamped, attributable records are.",
    whatGoodLooksLike:
      "Incident records are timestamped, attributable and exportable per customer. Status pages and customer notifications run on a rehearsed cadence, and the obligations inside enterprise contracts are inventoried and reviewed at renewal.",
    supportsLine:
      "PagerDuty covers response and evidence: coordinated multi-tenant incident response, with a timestamped record you can pull per customer.",
    resource: null,
  },
};

export var DOMAIN_CANON = [
  "svc",
  "peak",
  "cust",
  "ops",
  "security",
  "cps234",
  "soci",
  "privacy",
  "ai",
];

export var DOMAIN_OVERRIDES = {
  other: {
    ops: {
      deadline:
        "Know your critical operations, their dependencies and the disruption tolerance for each - and rehearse recovery against it.",
    },
    security: {
      deadline:
        "Confirm the incident-notification windows that apply where you operate, and rehearse meeting them out of hours.",
    },
    privacy: {
      deadline:
        "Map your breach assessment and notification duties, including who decides and who signs off.",
    },
    ai: {
      deadline:
        "Keep a live inventory of AI use cases with named owners, and a tested fallback for each one sitting in a critical path.",
    },
    svc: {
      deadline:
        "Confirm your service-continuity and emergency-call obligations, and rehearse customer communication alongside the technical response.",
    },
    peak: {
      deadline:
        "Agree a cost-per-minute threshold for degraded checkout, and make breaching it escalate automatically.",
    },
    cust: {
      deadline:
        "Inventory the obligations flowing down through enterprise contracts, and keep incident evidence exportable per customer.",
    },
  },
  au: {
    privacy: {
      name: "Privacy Act",
      subtitle: "Breach response",
      radarLines: ["Privacy", "Act"],
      deadline:
        "In effect - Eligible data breaches must be notified to the OAIC and affected individuals.",
      regRisk:
        "Recent reforms significantly increase penalties and enforcement scrutiny. A slower response can make a breach harder to contain and harder to scope.",
      resource: {
        label: "OAIC Notifiable Data Breaches Guidance",
        url: "https://www.oaic.gov.au/privacy/notifiable-data-breaches",
      },
    },
  },
  sg: {
    privacy: {
      name: "PDPA",
      subtitle: "Data breach response",
      radarLines: ["PDPA"],
      deadline:
        "In effect - Notifiable data breaches must be reported to the PDPC within 3 calendar days of assessment.",
      regRisk:
        "The PDPA data breach notification regime requires assessment and notification on a defined clock, with financial penalties scaled to turnover. Response speed directly influences scope, impact and exposure.",
      resource: {
        label: "PDPC Data Breach Management Guidance",
        url: "https://www.pdpc.gov.sg/help-and-resources/2021/01/data-breach-management-guide",
      },
    },
  },
  nz: {
    privacy: {
      name: "Privacy Act 2020",
      subtitle: "Breach response",
      radarLines: ["Privacy", "Act 2020"],
      deadline:
        "In effect - Notifiable privacy breaches must be reported to the OPC as soon as practicable.",
      regRisk:
        "The Privacy Act 2020 requires notifiable breaches to be reported to the OPC and affected individuals as soon as practicable. Response speed directly influences scope, impact and exposure.",
      resource: {
        label: "OPC Privacy Breach Guidance",
        url: "https://www.privacy.org.nz/responsibilities/privacy-breaches/",
      },
    },
  },
  hk: {
    privacy: {
      name: "PDPO",
      subtitle: "Data breach response",
      radarLines: ["PDPO"],
      deadline:
        "Voluntary today - the PCPD recommends notifying significant breaches, and a mandatory regime is under discussion.",
      regRisk:
        "Breach notification to the PCPD is voluntary today, but the PCPD expects significant breaches to be reported and a mandatory regime is under discussion. Your enterprise customers and overseas regulators expect notification regardless, and a slower response can make a breach harder to contain and harder to scope.",
      resource: {
        label: "PCPD - Privacy Commissioner for Personal Data",
        url: "https://www.pcpd.org.hk/",
      },
    },
  },
  my: {
    privacy: {
      name: "PDPA",
      subtitle: "Data breach response",
      radarLines: ["PDPA"],
      deadline:
        "In effect - breaches reported to the PDP Commissioner within 72 hours; affected individuals within 7 days where there is significant harm.",
      regRisk:
        "The amended PDPA brought mandatory breach notification into force in 2025: 72 hours to the PDP Commissioner, seven days to affected individuals where harm is significant. Response speed directly influences scope, impact and exposure.",
      resource: {
        label: "Personal Data Protection Department",
        url: "https://www.pdp.gov.my/",
      },
    },
  },
  th: {
    privacy: {
      name: "PDPA",
      subtitle: "Data breach response",
      radarLines: ["PDPA"],
      deadline:
        "In effect - breaches must be reported to the PDPC without delay, and within 72 hours where feasible.",
      regRisk:
        "Thailand's PDPA requires breach notification to the PDPC within 72 hours where feasible, and enforcement has been active since 2024. Response speed directly influences scope, impact and exposure.",
      resource: { label: "PDPC Thailand", url: "https://www.pdpc.or.th/" },
    },
  },
  kr: {
    privacy: {
      name: "PIPA",
      subtitle: "Data breach response",
      radarLines: ["PIPA"],
      deadline:
        "In effect - affected individuals notified within 72 hours; the PIPC too where thresholds are met.",
      regRisk:
        "PIPA's 2023 amendment put breach response on a uniform 72-hour clock: affected individuals within 72 hours, and the PIPC where thresholds such as 1,000 or more subjects are met. Response speed directly influences scope, impact and exposure.",
      resource: {
        label: "PIPC - Personal Information Protection Commission",
        url: "https://www.pipc.go.kr/",
      },
    },
    ai: {
      regRisk:
        "Korea's AI Basic Act took effect in January 2026: high-impact AI carries statutory duties on risk management, human oversight and user notification, with administrative fines phasing in behind a grace period. When an AI feature misbehaves inside a critical operation, that is now a legal question as well as an operational one.",
    },
  },
  "au-fsi": {
    ops: {
      name: "CPS 230",
      subtitle: "Operational risk management",
      radarLines: ["CPS 230"],
      deadline: dated(
        "2027-07-01",
        "July 2025 - CPS 230 is in effect: disruptions outside tolerance notified to APRA within 24 hours, material incidents within 72.",
        "In effect since July 2025 - disruptions outside tolerance notified to APRA within 24 hours, material incidents within 72, with tolerances tracked and evidenced.",
      ),
      regRisk:
        "CPS 230 strengthens expectations around operational resilience and board accountability - and FAR assigns defined responsibilities to named accountable persons, who must take reasonable steps to meet them. Demonstrable tolerance monitoring, testing, and governance oversight are now core supervisory priorities.",
      whatGoodLooksLike:
        "Keep the service catalogue current, with an owner and a defined tolerance for each critical operation. Map those operations to the systems, people and third parties they depend on, with tracking against tolerances aligned to board-approved overall levels. Documentation is current, tested, and audit-ready.",
      resource: {
        label: "APRA CPS 230 Prudential Standard",
        url: "https://handbook.apra.gov.au/standard/cps-230",
      },
    },
  },
  "au-fsi-lite": {
    ops: {
      regRisk:
        "Operational resilience expectations don't stop at the prudential perimeter. Licence and reporting obligations still hinge on the same capability: knowing what broke, fixing it fast, and proving your response afterwards.",
    },
  },
  "sg-fsi": {
    ops: {
      name: "Operational resilience",
      subtitle: "Critical operations & outsourcing (MAS)",
      regRisk:
        "MAS' operational risk management expectations put board-level accountability on critical operations, tolerances and third-party dependencies - and the outsourcing guidelines flow those expectations into vendor contracts.",
      resource: {
        label: "MAS Guidelines on Risk Management",
        url: "https://www.mas.gov.sg/regulation",
      },
    },
    security: {
      name: "MAS TRM & notification",
      subtitle: "Technology risk & incident reporting",
      radarLines: ["MAS TRM"],
      deadline:
        "In effect - MAS expects notification within 1 hour of discovering a relevant incident.",
      regRisk:
        "MAS expects notification within one hour of discovering a relevant incident, and the Technology Risk Management guidelines set explicit expectations for detection, escalation and recovery. One hour leaves no room for manual triage.",
      resource: {
        label: "MAS Technology Risk Management Guidelines",
        url: "https://www.mas.gov.sg/regulation/guidelines/technology-risk-management-guidelines",
      },
    },
  },
  "nz-fsi": {
    security: {
      name: "RBNZ cyber resilience",
      subtitle: "Cyber resilience & incident reporting",
      radarLines: ["RBNZ cyber", "resilience"],
      deadline:
        "In effect - material cyber incidents reported to RBNZ within 72 hours.",
      regRisk:
        "The Reserve Bank asks regulated entities to self-assess against its cyber resilience guidance and report material incidents within 72 hours. The guidance reads like a checklist of detection, escalation and recovery capability.",
      resource: {
        label: "RBNZ Cyber Resilience Guidance",
        url: "https://www.rbnz.govt.nz/regulation-and-supervision/cross-sector-oversight/cyber-resilience",
      },
    },
  },
  "hk-fsi": {
    ops: {
      name: "HKMA OR-2",
      subtitle: "Operational resilience",
      radarLines: ["HKMA", "OR-2"],
      deadline: dated(
        "2027-06-01",
        "31 May 2026 has passed - authorised institutions are expected to be operationally resilient now.",
        "In effect - SPM OR-2 operational-resilience requirements apply to authorised institutions.",
      ),
      regRisk: dated(
        "2027-06-01",
        "SPM OR-2 required authorised institutions to be operationally resilient by 31 May 2026: critical operations mapped, tolerances set, severe-but-plausible scenarios tested. Now the deadline has passed, supervisors want to see the evidence.",
        "SPM OR-2 requires authorised institutions to be operationally resilient: critical operations mapped, tolerances set, severe-but-plausible scenarios tested. Supervisors want to see the evidence.",
      ),
      whatGoodLooksLike:
        "Keep a live map of critical operations with board-approved tolerances, and track disruptions against them as they happen. Recovery evidence is current, rehearsed and ready for an audit.",
      resource: {
        label: "HKMA SPM OR-2 Operational Resilience",
        url: "https://www.hkma.gov.hk/media/eng/doc/key-functions/banking-stability/supervisory-policy-manual/OR-2.pdf",
      },
    },
    security: {
      name: "Cyber & incident reporting",
      subtitle: "C-RAF, TM-E-1 & Cap. 653",
      radarLines: ["Cyber &", "reporting"],
      deadline:
        "In effect - Cap. 653 requires serious incidents reported within 12 hours; C-RAF 2.0 sets the cyber bar.",
      regRisk:
        "C-RAF 2.0 assessments, TM-E-1 expectations and Cap. 653's 12-hour clock all test the same capability: detect, escalate, report. Twelve hours leaves little room for manual triage.",
      resource: {
        label: "Cap. 653 - Protection of Critical Infrastructures Ordinance",
        url: "https://www.elegislation.gov.hk/hk/cap653",
      },
    },
    ai: {
      regRisk:
        "The HKMA has set out supervisory expectations for banks' use of generative AI, and the PCPD has published AI governance guidance. When an AI feature misbehaves inside a critical operation, the same OR-2 tolerances apply - and someone has to notice in time.",
    },
  },
  "hk-telco": {
    svc: {
      deadline:
        "In effect - serious incidents to the Commissioner under Cap. 653 within 12 hours; other incidents within 48.",
      regRisk:
        "Communications is a designated sector under Cap. 653: serious computer-system incidents must be reported within 12 hours, others within 48, and the Code of Practice spells out what operators must have in place. OFCA still expects networks to stay up and customers to be kept informed.",
      resource: {
        label: "Cap. 653 - Protection of Critical Infrastructures Ordinance",
        url: "https://www.elegislation.gov.hk/hk/cap653",
      },
    },
    ops: {
      regRisk:
        "Cap. 653 requires designated operators to run computer-system security management - knowing your critical network operations and their dependencies is the entry ticket, and you can't hit a 12-hour reporting clock if detection and escalation are manual.",
    },
  },
  "hk-digital": {
    cust: {
      regRisk:
        "HKMA operational-resilience and outsourcing expectations flow through your bank customers' contracts - their OR-2 tolerances become your service commitments, and their auditors want timestamped evidence of YOUR incident response. Cap. 653 can also reach large digital infrastructure directly if designated.",
    },
  },
  "hk-generic": {
    ops: {
      regRisk:
        "Regulators and enterprise customers increasingly expect organisations to know their critical operations, set tolerances for disruption, and prove they can recover within them. Cap. 653 puts that expectation into law for designated critical infrastructure - with a 12-hour reporting clock attached.",
    },
  },
  "my-fsi": {
    ops: {
      name: "Operational resilience",
      subtitle: "Technology risk (BNM RMiT)",
      regRisk:
        "BNM's RMiT holds boards accountable for technology risk, with prescriptive expectations on availability, security operations and incident response. When something breaks, BNM expects you to know fast, recover fast and keep the records.",
      resource: {
        label: "BNM Risk Management in Technology (RMiT)",
        url: "https://www.bnm.gov.my/",
      },
    },
    security: {
      name: "Cyber & incident reporting",
      subtitle: "Cyber Security Act 2024 & RMiT",
      radarLines: ["Cyber &", "reporting"],
      deadline:
        "In effect - NCII entities notify NACSA immediately, with particulars within 6 hours.",
      regRisk:
        "The Cyber Security Act 2024 requires NCII entities to notify NACSA immediately on knowledge of an incident, with particulars within six hours and penalties up to RM500,000 behind it. Six hours leaves little room for manual triage.",
      resource: {
        label: "NACSA - Cyber Security Act 2024",
        url: "https://www.nacsa.gov.my/act854.php",
      },
    },
  },
  "my-telco": {
    svc: {
      deadline:
        "In effect - incidents to NACSA immediately, with particulars within 6 hours.",
      regRisk:
        "Communications is an NCII sector under the Cyber Security Act 2024: notify NACSA immediately, particulars within six hours, penalties attached. MCMC expectations on network continuity and customer communication sit on top.",
      resource: {
        label: "NACSA - Cyber Security Act 2024",
        url: "https://www.nacsa.gov.my/act854.php",
      },
    },
    ops: {
      regRisk:
        "The Cyber Security Act puts risk-management duties on NCII operators - knowing your critical network operations and their dependencies is the entry ticket, and you can't assemble incident particulars in six hours if the timeline isn't already being captured.",
    },
  },
  "my-digital": {
    cust: {
      regRisk:
        "Your enterprise customers answer to BNM, NACSA and their own boards - their obligations flow into your contracts, and their auditors want timestamped evidence of YOUR incident response.",
    },
  },
  "my-generic": {
    ops: {
      regRisk:
        "Regulators and enterprise customers increasingly expect organisations to know their critical operations, set tolerances for disruption, and prove they can recover within them. The Cyber Security Act 2024 makes that a legal duty for designated critical sectors, with a six-hour reporting clock attached.",
    },
  },
  "th-fsi": {
    ops: {
      name: "Operational resilience",
      subtitle: "IT risk (Bank of Thailand)",
      regRisk:
        "The Bank of Thailand's IT risk guidelines make technology risk a supervisory matter: governance, resilience and incident response all report up. When something breaks, the BOT expects you to know fast and evidence the response.",
      resource: { label: "Bank of Thailand", url: "https://www.bot.or.th/" },
    },
    security: {
      name: "Cyber & incident reporting",
      subtitle: "Cybersecurity Act & NCSA",
      radarLines: ["Cyber &", "reporting"],
      deadline:
        "In effect - CII incidents require an initial report to the NCSA within 24 hours.",
      regRisk:
        "Financial services is critical infrastructure under the Cybersecurity Act: the initial incident report is due to the NCSA and your regulator within 24 hours, with follow-up obligations behind it. A day disappears quickly in a live incident.",
      resource: { label: "NCSA Thailand", url: "https://www.ncsa.or.th/" },
    },
  },
  "th-telco": {
    svc: {
      deadline:
        "In effect - initial CII incident report to the NCSA within 24 hours.",
      regRisk:
        "Telecommunications is CII under the Cybersecurity Act: the initial report is due to the NCSA within 24 hours, and NBTC licence obligations on continuity and customer communication sit on top.",
      resource: { label: "NCSA Thailand", url: "https://www.ncsa.or.th/" },
    },
    ops: {
      regRisk:
        "The Cybersecurity Act requires CII operators to run risk management and incident readiness - knowing your critical network operations and their dependencies is the entry ticket.",
    },
  },
  "th-digital": {
    cust: {
      regRisk:
        "Your enterprise customers answer to the Bank of Thailand, the NCSA and their own regulators - their obligations flow into your contracts, and their auditors want timestamped evidence of YOUR incident response.",
    },
  },
  "th-generic": {
    ops: {
      regRisk:
        "Regulators and enterprise customers increasingly expect organisations to know their critical operations, set tolerances for disruption, and prove they can recover within them. The Cybersecurity Act adds a 24-hour reporting clock for designated critical infrastructure.",
    },
  },
  "kr-fsi": {
    ops: {
      name: "Operational resilience",
      subtitle: "E-finance incident regime (FSC/FSS)",
      regRisk:
        "Korea's electronic financial transactions regime makes incident response a supervisory matter: e-finance incidents carry dedicated reporting duties to the FSS, and operational failures draw regulatory attention fast. Knowing what broke, and evidencing the response, is the core capability.",
      resource: {
        label: "Financial Supervisory Service",
        url: "https://www.fss.or.kr/",
      },
    },
  },
  "kr-telco": {
    svc: {
      regRisk:
        "Korea wrote network-stability obligations into law for major carriers and platforms after high-profile outages: continuity planning, incident readiness and reporting are regulatory expectations, and customer communication is judged alongside the fix.",
      resource: {
        label: "Ministry of Science and ICT",
        url: "https://www.msit.go.kr/",
      },
    },
  },
  "kr-digital": {
    cust: {
      regRisk:
        "Your enterprise customers answer to regulators - and with the AI Basic Act in force, the AI in your platform is part of what their auditors ask about. Timestamped, attributable incident records are the answer they expect.",
    },
  },
  "au-telco": {
    svc: {
      deadline: dated(
        "2026-10-04",
        "MI1 due 3 October 2026 under the TSRMP framework. ECS rules carry penalties up to $30 million.",
        "MI1 fell due on 3 October 2026 under the TSRMP framework. ECS rules carry penalties up to $30 million.",
      ),
      regRisk: dated(
        "2026-10-04",
        "The ECS rules carry penalties up to $30 million for triple-zero failures, the Outage Communication Standard sets customer-communication clocks during major outages, and SOCI/TSRMP maturity indicator 1 falls due on 3 October 2026.",
        "The ECS rules carry penalties up to $30 million for triple-zero failures, the Outage Communication Standard sets customer-communication clocks during major outages, and SOCI/TSRMP maturity indicator 1 fell due on 3 October 2026 - telcos are now expected to show where they landed.",
      ),
      resource: {
        label: "ACMA Emergency Call Service Rules",
        url: "https://www.acma.gov.au/emergency-calls",
      },
    },
    ops: {
      regRisk:
        "The TSRMP framework brings telcos under SOCI-style risk management obligations, with maturity indicators on a published timetable. Knowing your critical network operations and their dependencies is the entry ticket.",
    },
  },
  "sg-telco": {
    svc: {
      deadline:
        "In effect - CII owners must report incidents to the CSA within 2 hours.",
      regRisk:
        "The Cybersecurity Act gives CII owners two hours to report an incident, and IMDA resilience codes set expectations for network continuity and customer communication. Two hours leaves no room for manual detection.",
      resource: {
        label: "CSA Cybersecurity Act Overview",
        url: "https://www.csa.gov.sg/legislation/cybersecurity-act",
      },
    },
  },
  "nz-telco": {
    svc: {
      regRisk:
        "TICSA obligations and the 111 Contact Code make emergency-call continuity and customer communication the first questions asked after any outage. When networks fail, 111 access is the headline.",
      resource: {
        label: "Commerce Commission 111 Contact Code",
        url: "https://comcom.govt.nz/regulated-industries/telecommunications",
      },
    },
  },
  "au-digital": {
    cust: {
      regRisk:
        "CPS 230 pushes your FSI customers' obligations into your contracts: material service providers must support tolerance monitoring, incident response and evidence on demand. Those requirements can also appear in your supplier contracts. Data storage and processing is also a SOCI sector in its own right, with CIRMP and reporting obligations.",
    },
  },
  "au-digital-lite": {
    cust: {
      regRisk:
        "Data storage and processing is a SOCI critical-infrastructure sector: CIRMP obligations and ACSC incident reporting apply where an asset meets the critical-asset threshold. Your enterprise customers' auditors expect timestamped evidence of your incident response.",
      resource: {
        label: "CISC Critical Infrastructure Obligations Guidance",
        url: "https://www.cisc.gov.au/legislation-regulation-and-compliance/soci-act-2018",
      },
    },
    ops: {
      regRisk:
        "The SOCI Act brings data storage and processing under critical-infrastructure risk management: knowing your critical operations, their dependencies, and proving you can recover within tolerance is the entry ticket.",
    },
  },
  "au-generic": {
    ops: {
      regRisk:
        "Regulators and enterprise customers increasingly expect organisations to know their critical operations, set tolerances for disruption, and prove they can recover within them. The Cyber Security Act 2024 adds mandatory ransomware-payment reporting, and SOCI obligations apply if you're deemed critical infrastructure.",
    },
  },
  "sg-digital": {
    cust: {
      regRisk:
        "The draft Digital Infrastructure Bill proposes resilience and incident-reporting obligations for major data centre and cloud providers - on top of MAS outsourcing rules already flowing through your FSI contracts.",
    },
  },
  "nz-digital": {
    cust: {
      regRisk:
        "The Reserve Bank's outsourcing policy (BS11) writes prescribed terms into large banks' vendor contracts - and their regulators are now asking about AI suppliers. Evidence of your incident response is part of the deal.",
    },
  },
};

export var FSI_DESCRIPTOR = {
  au: "banks, insurers, super, fintech & payments",
  sg: "banks, insurers, asset managers, fintech & payments",
  nz: "banks, insurers, KiwiSaver & fund managers, fintech",
  hk: "banks, insurers, asset managers, fintech & payments",
  my: "banks, insurers, asset managers, fintech & payments",
  th: "banks, insurers, asset managers, fintech & payments",
  kr: "banks, insurers, asset managers, fintech & payments",
  other: "banks, insurers, asset managers, fintech & payments",
};

export var INDUSTRY_NAMES = {
  fsi: "Financial services",
  telco: "Telecommunications",
  ecom: "Retail & ecommerce",
  digital: "Technology & digital",
  generic: "Cross-industry",
  other: "Cross-industry",
};

export var MATURITY_LABELS = ["Initial", "Developing", "Capable", "Leading"];

export var SLOTS = {
  "au-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "material service provider",
    SEC_REG: "APRA",
    SEC_CLOCK: "72-hour",
    CYBER_AGENCY: "the ACSC",
    CYBER_CLOCK: "12 hours",
    PRIVACY_BODY: "the OAIC",
    FRAMEWORK_TERM: "a tested CIRMP",
  },
  "au-fsi-lite": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: "the ACSC",
    CYBER_CLOCK: "12 hours",
    PRIVACY_BODY: "the OAIC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "au-telco": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "the ACSC",
    SEC_CLOCK: "72-hour",
    CYBER_AGENCY: "the ACSC",
    CYBER_CLOCK: "12 hours",
    PRIVACY_BODY: "the OAIC",
    FRAMEWORK_TERM: "a tested CIRMP",
    MAJOR_OUTAGE: "100,000+ services",
    ECS_PLAYBOOK: "covers wilting, camp-on checks and notifications",
  },
  "sg-telco": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "the CSA",
    SEC_CLOCK: "2-hour",
    CYBER_AGENCY: "the CSA",
    CYBER_CLOCK: "2 hours",
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
    MAJOR_OUTAGE: "a large share of your subscriber base",
    ECS_PLAYBOOK: "covers the required network and notification steps",
  },
  "nz-telco": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the OPC",
    FRAMEWORK_TERM: "a tested resilience programme",
    MAJOR_OUTAGE: "a large share of your subscriber base",
    ECS_PLAYBOOK: "covers the required network and notification steps",
  },
  "sg-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "nz-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the OPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "sg-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "nz-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the OPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "au-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the OAIC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "au-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the OAIC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "au-digital-lite": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "the ACSC",
    SEC_CLOCK: "required",
    CYBER_AGENCY: "the ACSC",
    CYBER_CLOCK: "12 hours",
    PRIVACY_BODY: "the OAIC",
    FRAMEWORK_TERM: "a tested CIRMP",
  },
  "au-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the OAIC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "sg-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "third-party service provider",
    SEC_REG: "MAS",
    SEC_CLOCK: "1-hour",
    CYBER_AGENCY: "MAS",
    CYBER_CLOCK: "1 hour",
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "sg-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "nz-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "critical service provider",
    SEC_REG: "RBNZ",
    SEC_CLOCK: "72-hour",
    CYBER_AGENCY: "RBNZ",
    CYBER_CLOCK: "72 hours",
    PRIVACY_BODY: "the OPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "nz-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the OPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "hk-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "third-party service provider",
    SEC_REG: "the HKMA",
    SEC_CLOCK: "immediate",
    CYBER_AGENCY: "the Commissioner under Cap. 653",
    CYBER_CLOCK: "12 hours",
    PRIVACY_BODY: "the PCPD",
    FRAMEWORK_TERM: "a tested operational resilience framework",
  },
  "hk-telco": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "Cap. 653",
    SEC_CLOCK: "12-hour",
    CYBER_AGENCY: "the Commissioner under Cap. 653",
    CYBER_CLOCK: "12 hours",
    PRIVACY_BODY: "the PCPD",
    FRAMEWORK_TERM: "a tested resilience programme",
    MAJOR_OUTAGE: "a large share of your subscriber base",
    ECS_PLAYBOOK: "covers the required network and notification steps",
  },
  "hk-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PCPD",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "hk-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PCPD",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "hk-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PCPD",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "my-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "third-party service provider",
    SEC_REG: "Act 854",
    SEC_CLOCK: "6-hour",
    CYBER_AGENCY: "NACSA",
    CYBER_CLOCK: "6 hours",
    CYBER_LINE:
      "Notify NACSA immediately; incident particulars within 6 hours.",
    CYBER_Q:
      "Could you notify NACSA immediately and file incident particulars within 6 hours - including at 2 AM on a long weekend?",
    PRIVACY_BODY: "the PDP Commissioner",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "my-telco": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "Act 854",
    SEC_CLOCK: "6-hour",
    CYBER_AGENCY: "NACSA",
    CYBER_CLOCK: "6 hours",
    CYBER_LINE:
      "Notify NACSA immediately; incident particulars within 6 hours.",
    CYBER_Q:
      "Could you notify NACSA immediately and file incident particulars within 6 hours - including at 2 AM on a long weekend?",
    PRIVACY_BODY: "the PDP Commissioner",
    FRAMEWORK_TERM: "a tested resilience programme",
    MAJOR_OUTAGE: "a large share of your subscriber base",
    ECS_PLAYBOOK: "covers the required network and notification steps",
  },
  "my-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDP Commissioner",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "my-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDP Commissioner",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "my-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDP Commissioner",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "th-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "third-party service provider",
    SEC_REG: "the NCSA",
    SEC_CLOCK: "24-hour",
    CYBER_AGENCY: "the NCSA",
    CYBER_CLOCK: "24 hours",
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "th-telco": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "the NCSA",
    SEC_CLOCK: "24-hour",
    CYBER_AGENCY: "the NCSA",
    CYBER_CLOCK: "24 hours",
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
    MAJOR_OUTAGE: "a large share of your subscriber base",
    ECS_PLAYBOOK: "covers the required network and notification steps",
  },
  "th-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "th-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "th-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PDPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "kr-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "third-party service provider",
    SEC_REG: "the FSS",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PIPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "kr-telco": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PIPC",
    FRAMEWORK_TERM: "a tested resilience programme",
    MAJOR_OUTAGE: "a large share of your subscriber base",
    ECS_PLAYBOOK: "covers the required network and notification steps",
  },
  "kr-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PIPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "kr-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PIPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "kr-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "the PIPC",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "other-fsi": {
    APPROVAL_BODY: "board",
    VENDOR_TERM: "critical service provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "your privacy regulator",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "other-telco": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "your privacy regulator",
    FRAMEWORK_TERM: "a tested resilience programme",
    MAJOR_OUTAGE: "a large share of your subscriber base",
    ECS_PLAYBOOK: "covers the required network and notification steps",
  },
  "other-ecom": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "payment or platform provider",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "your privacy regulator",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "other-digital": {
    APPROVAL_BODY: "executive",
    VENDOR_TERM: "critical supplier",
    SEC_REG: "your customers' regulators",
    SEC_CLOCK: "contractually required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "your privacy regulator",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  "other-generic": {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "your privacy regulator",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
  default: {
    APPROVAL_BODY: "leadership",
    VENDOR_TERM: "critical vendor",
    SEC_REG: "your regulator",
    SEC_CLOCK: "required",
    CYBER_AGENCY: null,
    CYBER_CLOCK: null,
    PRIVACY_BODY: "your privacy regulator",
    FRAMEWORK_TERM: "a tested resilience programme",
  },
};

export var SPINE = [
  {
    id: "S1",
    sheetId: 1,
    domain: "ops",
    tier: "answers",
    text: "Could you produce a map of your critical operations and their dependencies by Friday?",
    anchors: [
      "It would take significant time, *documentation is fragmented* across teams.",
      "Critical services are known, but *not formally documented*.",
      "*Mostly mapped*, with some gaps in third-party or vendor dependencies.",
      "*Documented, current*, with clear ownership assigned to each critical operation.",
    ],
  },
  {
    id: "S2",
    sheetId: 2,
    domain: "ops",
    tier: "answers",
    text: "Has your {APPROVAL_BODY} set quantitative tolerance levels for disruptions to critical services?",
    anchors: [
      "Tolerance levels have *not yet been formally defined*.",
      "There is an informal understanding, but *nothing {APPROVAL_BODY}-approved*.",
      "Defined for *some services*, though not consistently measured.",
      "{APPROVAL_BODY}-approved tolerance levels, *tracked and reported in real time*.",
    ],
  },
  {
    id: "S3",
    sheetId: 3,
    domain: "ops",
    tier: "answers",
    text: "A {VENDOR_TERM} has a major outage. What does your first hour look like?",
    anchors: [
      "First awareness would likely rely on *customer reports*.",
      "Status pages would be checked *manually* once awareness emerges.",
      "*Alerts are received*, though response depends on availability.",
      "Automated detection with *predefined playbooks* and clear escalation contacts.",
    ],
  },
  {
    id: "S4",
    sheetId: 4,
    domain: "security",
    tier: "answers",
    text: "A security incident occurs. How confident are you in meeting {SEC_REG}'s {SEC_CLOCK} notification requirement?",
    anchors: [
      "Detection within the window would *not be assured*.",
      "Detection would occur, but notification preparation would be *challenging*.",
      "A *defined process* exists and has been exercised before.",
      "Playbooks are *tested*, templates prepared, and notification chains rehearsed.",
    ],
  },
  {
    id: "S5",
    sheetId: 5,
    domain: "security",
    tier: "answers",
    text: "How mature is your security alert pipeline?",
    anchors: [
      "Alert volumes are high and *signal-to-noise is low*.",
      "Alerts are generated, though triage is *manual and inconsistent*.",
      "Tuned for critical services with *some automation* in place.",
      "*Correlated, deduplicated*, and automatically escalated to the appropriate responder.",
    ],
  },
  {
    id: "S6",
    sheetId: 6,
    domain: "security",
    tier: "answers",
    text: null,
    anchors: [
      "Confidence in meeting the window is *low*.",
      "Business-hours response is reliable, though *out-of-hours coverage is limited*.",
      "On-call coverage exists, but reporting workflows are *not routinely rehearsed*.",
      "Out-of-hours response is *tested*, with reporting templates and contacts pre-prepared.",
    ],
  },
  {
    id: "S7",
    sheetId: 8,
    domain: "privacy",
    tier: "supports",
    text: "Personal data is exfiltrated. How quickly can you determine scope and contain the issue?",
    anchors: [
      "Scoping and containment would likely take *several days*.",
      "Initial detection would be fast, though full scoping would *take time*.",
      "Containment is rapid, with scoping completed *within 24 hours* in most scenarios.",
      "*Automated containment triggers* and forensic tooling enable scoping within hours.",
    ],
  },
  {
    id: "S8",
    sheetId: 9,
    domain: "privacy",
    tier: "supports",
    text: "Is your breach notification process mapped and ready - including {PRIVACY_BODY} and affected individuals?",
    anchors: [
      "Notification requirements would be assessed *reactively*.",
      "Legal requirements are understood, though workflows are *not predefined*.",
      "*Notification templates exist* and roles are clearly assigned.",
      "An *end-to-end, tested playbook* including communications templates and legal sign-off workflows.",
    ],
  },
  {
    id: "S9",
    sheetId: 20,
    domain: "ops",
    tier: "answers",
    text: "During a major incident, how much of your response runs without manual effort?",
    anchors: [
      "Response is *primarily manual*, with limited structured follow-up.",
      "Alerts create tickets, but diagnostics and remediation *remain manual*.",
      "Diagnostics and selected remediation steps are *automated for known scenarios*.",
      "*Automated remediation* for repeatable failures, with structured human oversight.",
    ],
  },
  {
    id: "S10",
    sheetId: 13,
    domain: "security",
    tier: "answers",
    text: "If a regulator requested evidence of your last 10 critical incidents, could you provide it?",
    anchors: [
      "Evidence would be *difficult to consolidate* into a coherent format.",
      "Records exist across *multiple systems and informal channels*.",
      "Incident records are largely available but *not consistently structured*.",
      "*Timestamped and attributable*, exportable from a single system of record.",
    ],
  },
  {
    id: "S11",
    sheetId: 15,
    domain: "ops",
    tier: "supports",
    text: "Could you demonstrate to an auditor that your BCP has been tested in the last 12 months?",
    anchors: [
      "A BCP exists but has *not been formally tested*.",
      "A *tabletop exercise* was conducted, though not a full scenario test.",
      "A *full scenario test* was conducted and findings documented.",
      "*Regular scenario testing*, including third-party failure and out-of-hours activation, with a documented evidence trail.",
    ],
  },
  {
    id: "S12",
    sheetId: 16,
    domain: "security",
    tier: "answers",
    text: "Do you have 24/7 operational coverage across all hazard types: cyber, physical, supply chain and personnel?",
    anchors: [
      "Cyber risk is covered. Other hazards are inconsistent. On-call is *informal*, with no structured rota or escalation.",
      "Most hazards are addressed, though in *silos*. On-call rosters exist, but coverage gaps remain.",
      "All hazards are covered under a *coordinated programme*. Defined 24/7 rotas operate with clear escalation.",
      "*All-hazards 24/7 coverage* under {FRAMEWORK_TERM}. Scheduling is automated and integrated with alerting and runbooks.",
    ],
  },
  {
    id: "AI1",
    sheetId: 17,
    domain: "ai",
    tier: "informs",
    text: "Could you produce a list of every AI tool and use case in the business, including AI features inside vendor platforms?",
    anchors: [
      "*No central list* exists; usage is known informally.",
      "Approved tools are listed, but vendor-embedded AI is *not tracked*.",
      "An inventory exists, though ownership is *not assigned* for each use case.",
      "A *live inventory with named owners*, covering vendor and staff AI use.",
    ],
  },
  {
    id: "AI2",
    sheetId: 18,
    domain: "ai",
    tier: "answers",
    text: "An AI feature supporting a critical operation starts producing unreliable outputs. What happens next?",
    anchors: [
      "We would likely learn of it from *customer or staff reports*.",
      "Someone would intervene, but the fallback is *improvised*.",
      "A documented fallback exists but has *not been tested*.",
      "*Automated detection*, escalation to a human, and a tested fallback path.",
    ],
  },
  {
    id: "AI3",
    sheetId: 19,
    domain: "ai",
    tier: "supports",
    text: "How would you know an AI provider had updated a model or its performance had degraded?",
    anchors: [
      "We *wouldn't know* unless the provider told us.",
      "We would notice through downstream issues *after the fact*.",
      "Output monitoring exists for some use cases, checked *manually*.",
      "*Continuous monitoring* with alert thresholds on behaviour and performance.",
    ],
  },
];

export var readinessTiers = [
  {
    min: 0,
    max: 25,
    label: "Significant gaps",
    colour: "#DC2626",
    bg: "#FEF2F2",
    description:
      "The responses indicate material gaps across the frameworks assessed. The areas below may require review or remediation.",
  },
  {
    min: 26,
    max: 50,
    label: "Early progress",
    colour: "#F59E0B",
    bg: "#FFFBEB",
    description:
      "Some of the assessed practices appear to be in place, but their application looks inconsistent. The gaps below may warrant review before your next audit.",
  },
  {
    min: 51,
    max: 75,
    label: "Operationally capable",
    colour: "#048A24",
    bg: "#F0FDF4",
    description:
      "Your responses indicate that many of the assessed practices are in place. The gaps identified below are worth reviewing, particularly around evidence and consistency.",
  },
  {
    min: 76,
    max: 100,
    label: "Advanced readiness",
    colour: "#048A24",
    bg: "#F0FDF4",
    description:
      "Your responses indicate documented and tested practices across most of the areas assessed. Review the detail below before relying on the result.",
  },
];
